import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { SectionHeader } from "../shared/SectionHeader";
import { iconMap } from "../shared/iconMap";

// [Journey] [SEO/GEO] Five genuine services as a centred pill switcher + panel. Hash anchors select a pathway.
// Production: render all five panels in server HTML (Bricks tabs) so every service is crawlable.
const MATERIALS_IMAGE = "https://cdn.magicpatterns.com/patterns/generated-images/2a2be1ce-46f3-401a-b509-13a9a43b5699.jpg";
const services = [{
  id: "home-designs-service",
  title: "Single-storey designs",
  who: "Ideal for downsizers and anyone planning for easy, single-level living.",
  description: "Thoughtfully designed single-storey homes that combine practicality, quality and lasting value, offering a streamlined path to building. Each design can be tailored to suit you.",
  features: ["Single-level living with no stairs", "3 and 4 bedroom layouts with separate theatre", "Site fit reviewed with you before you commit"],
  link: {
    label: "Browse home designs",
    to: "/home-designs"
  },
  icon: "Home"
}, {
  id: "knock-down-rebuild",
  title: "Knock-down/rebuild",
  who: "Ideal if you love your street but your current home no longer suits you.",
  description: "Love your location but not your home? Replace your existing house with a modern, energy-efficient home designed for the way you live today.",
  features: ["Stay in the neighbourhood you know", "Site considerations discussed early", "Duplex rebuild underway in Dianella"],
  link: {
    label: "Talk about your block",
    to: "/contact?project=knockdown"
  },
  icon: "RefreshCw"
}, {
  id: "rear-block-homes",
  title: "Rear-block homes",
  who: "Ideal if you’ve subdivided, or are thinking about it, and want a new home out the back.",
  description: "Unlock the value of your land with a custom rear-block home, designed to make the most of your site while maximising space and functionality.",
  features: ["Access and orientation planned early", "Design shaped to the lot", "Talk it through before you subdivide"],
  link: {
    label: "Ask about a rear block",
    to: "/contact?project=rear-block"
  },
  icon: "MapPinned"
}, {
  id: "custom-homes",
  title: "Custom homes",
  who: "Ideal if you know exactly how you want to live, and no standard plan quite fits.",
  description: "Create a home that’s uniquely yours. We work closely with you to design a home that reflects your lifestyle, vision, budget and long-term goals, working directly with our designers and build team from the start.",
  features: ["Starts with your block and your brief", "Scope and cost of design work agreed upfront", "Project-specific costing before contract"],
  link: {
    label: "Discuss a custom home",
    to: "/contact?project=custom"
  },
  icon: "PencilRuler"
}, {
  id: "developments",
  title: "Investments & developments",
  who: "Ideal for investors and developers who want a builder that invests too.",
  description: "From duplexes to multi-lot projects, we deliver development solutions that make the most of your land and its buildability. We invest in developments of our own, so we understand the decisions from your side.",
  features: ["Owner-investor perspective", "Investor clients who come back for a second build", "Duplex development underway in Dianella"],
  link: {
    label: "Explore an investment build",
    to: "/contact?project=development"
  },
  icon: "Building2"
}];
export function BuildPathways() {
  const location = useLocation();
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  useEffect(() => {
    const index = services.findIndex(service => `#${service.id}` === location.hash);
    if (index >= 0) setActive(index);
  }, [location.hash]);
  const current = services[active];
  const CurrentIcon = iconMap[current.icon];
  return <section id="services" aria-labelledby="services-heading" className="bg-paper py-20 md:py-28" data-id="element-144" data-mp="2be94mzyx34im">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-145" data-mp="2q3580jq13tcw">
        <SectionHeader id="services-heading" title="However you’re making your next move." intro="Five ways to build with us, each held to the same standard and backed by the same owners." data-id="element-146" data-mp="2l53q424ca030" />

        <div role="tablist" aria-label="What we build" className="mt-12 flex flex-wrap justify-center gap-2" data-id="element-147" data-mp="1sh7xb8hw4sbi">
          {services.map((service, index) => {
          const selected = index === active;
          return <span key={service.id} id={service.id} className="scroll-mt-32" data-id="element-148" data-mp="22d174k4867pl" data-mpi={index}>
                <button type="button" role="tab" aria-selected={selected} aria-controls="pathway-panel" onClick={() => setActive(index)} className={`min-h-[48px] whitespace-nowrap rounded-full border px-5 text-[15px] font-semibold transition-[background-color,color,border-color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean ${selected ? "border-charcoal bg-charcoal text-paper" : "border-line bg-white text-charcoal hover:border-charcoal"}`} data-id="element-149" data-mp="3irt1h2kxlp1j">
                  {service.title}
                </button>
              </span>;
        })}
        </div>

        <div id="pathway-panel" role="tabpanel" className="mt-10 overflow-hidden rounded-[32px] border border-line bg-white" data-id="element-150" data-mp="3vbzpiyf8nznc">
          <AnimatePresence mode="wait" initial={false} data-id="element-151" data-mp="36cngn8sd5ai">
            <motion.div key={current.id} initial={reduce ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid md:grid-cols-2" data-id="element-152" data-mp="qx9lqx7ns43a">
              <div className="relative min-h-[240px] md:min-h-[460px]" data-id="element-153" data-mp="11j4xq6sdr4hb">
                <img src={MATERIALS_IMAGE} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-154" data-mp="wi7k87ibeym5" />
                <span className="absolute left-5 top-5 flex h-14 w-14 items-center justify-center rounded-full bg-white text-charcoal shadow-sm" data-id="element-155" data-mp="rjxlxo1anhwb">
                  <CurrentIcon className="h-6 w-6" aria-hidden="true" data-id="element-156" data-mp="humcu1eeqd8f" />
                </span>
              </div>
              <div className="flex flex-col p-7 md:p-12" data-id="element-157" data-mp="drzk7y9wwoa6">
                <h3 className="text-[28px] font-semibold leading-tight tracking-tight md:text-[34px]" data-id="element-158" data-mp="dqenq341ev9o">{current.title}</h3>
                <p className="mt-3 rounded-2xl bg-paper px-4 py-3 text-[15px] font-medium text-charcoal" data-id="element-159" data-mp="3ubmr0kx92ob">{current.who}</p>
                <p className="mt-5 text-[17px] leading-relaxed text-slate" data-id="element-160" data-mp="3v0mlgq2glrch">{current.description}</p>
                <ul className="mt-6 space-y-3 text-[16px]" data-id="element-161" data-mp="3kxr1bpn15z84">
                  {current.features.map(feature => <li key={feature} className="flex items-start gap-3" data-id="element-162" data-mp="3pur862rpf60a">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-163" data-mp="3uuvp1twiy2r1">
                        <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-164" data-mp="3o2nuq1qe3mi" />
                      </span>
                      {feature}
                    </li>)}
                </ul>
                <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-3 pt-8" data-id="element-165" data-mp="375uadox3lvdb">
                  <ButtonLink to={current.link.to} data-id="element-166" data-mp="3c32vnydjmfis">
                    {current.link.label} <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-167" data-mp="3gvmaszrvpipp" />
                  </ButtonLink>
                  <Link to="/projects" className="text-[15px] font-medium underline underline-offset-4" data-id="element-168" data-mp="2jexbbcqeisjh">See related work</Link>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}